import { Phone, MapPin, Navigation, TrainFront, Landmark } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";

const LANDMARKS = [
  {
    icon: TrainFront,
    title: "Ameerpet Metro",
    text: "A short ride from Ameerpet Metro Station — one of the easiest ways to reach us.",
  },
  {
    icon: Landmark,
    title: "Divyashakti Apartments",
    text: "502, Greenlands Road — inside Divyashakti Appartments, Ameerpet.",
  },
  {
    icon: MapPin,
    title: "Greenlands Road",
    text: "Centrally located in Ameerpet, easy access from Hyderabad's business district.",
  },
];

const Contact = () => {
  return (
    <>
      <SEO
        title="Contact & Location | The Soul Skin Spa & Salon"
        description="Visit The Soul Skin Spa & Salon at 502, Greenlands Rd, Divyashakti Appartments, Ameerpet, Hyderabad. Call +91 7995280296 — no forms, just call to book."
        path="/contact"
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section className="relative pt-40 pb-20 sm:pt-48 sm:pb-24 bg-ss-bgAlt border-b border-ss-gold/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-ss-gold mb-5">
              Find Us
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ss-text max-w-2xl">
              Visit Our Sanctuary in Ameerpet
            </h1>
            <p className="mt-4 text-ss-textMuted max-w-lg text-base sm:text-lg">
              No forms, no waiting — just call or WhatsApp us directly to book
              your ritual.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTACT INFO + MAP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Reveal className="bg-ss-surface border border-ss-gold/10 rounded-2xl p-8 sm:p-10 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-ss-gold mb-6">
              Direct Contact
            </p>

            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-ss-gold text-ss-bg shrink-0 group-hover:bg-ss-goldHover transition-colors">
                <Phone className="w-5 h-5" />
              </span>
              <span className="font-heading text-2xl sm:text-3xl font-bold text-ss-text group-hover:text-ss-gold transition-colors">
                {BUSINESS.phoneDisplay}
              </span>
            </a>

            <div className="flex items-start gap-4 mt-8">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-ss-bgAlt border border-ss-gold/20 text-ss-gold shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <p
                data-testid="contact-address-text"
                className="text-ss-text text-base leading-relaxed pt-1"
              >
                {BUSINESS.address}
              </p>
            </div>
          </div>

          <a
            href={BUSINESS.mapsDirectionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-directions-button"
            className="inline-flex items-center justify-center gap-2.5 bg-ss-gold text-ss-bg px-7 py-4 rounded-full text-sm font-semibold mt-10 hover:bg-ss-goldHover transition-all duration-300 w-fit"
          >
            <Navigation className="w-4 h-4" /> Get Directions
          </a>
        </Reveal>

        <Reveal delay={0.1} className="rounded-2xl overflow-hidden min-h-[380px] border border-ss-gold/10">
          <iframe
            data-testid="contact-map-embed"
            title="The Soul Skin Spa & Salon Location"
            src={BUSINESS.mapsEmbedSrc}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: "380px" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>
      </section>

      {/* LANDMARKS */}
      <section className="bg-ss-bgAlt border-y border-ss-gold/10 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-ss-gold mb-4">
              Getting Here
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ss-text max-w-xl">
              Nearby landmarks & directions
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            {LANDMARKS.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.08}>
                <div className="bg-ss-surface rounded-2xl p-7 h-full border border-ss-gold/10">
                  <l.icon className="w-7 h-7 text-ss-gold" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-semibold text-ss-text mt-5">
                    {l.title}
                  </h3>
                  <p className="text-sm text-ss-textMuted mt-2 leading-relaxed">
                    {l.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-28 text-center">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ss-text">
            We'd love to welcome you
          </h2>
          <a
            href={`tel:${BUSINESS.phoneClean}`}
            data-testid="contact-final-call-button"
            className="inline-flex items-center gap-2.5 bg-ss-gold text-ss-bg px-8 py-4 rounded-full text-sm font-semibold mt-8 hover:bg-ss-goldHover transition-all duration-300"
          >
            <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
          </a>
        </Reveal>
      </section>
    </>
  );
};

export default Contact;
